/**
 * @see https://chakra-ui.com/docs/styled-system/component-style
 * @see https://chakra-ui.com/docs/styled-system/advanced-theming
 */

import { layerStyles, textStyles } from './styles'
import { fontSizes, htmlBaseLineHeight, space } from './tokens'
import { sliderTheme } from './sliderTheme'

export const blockSizes = {
  xxxs: {
    px: ['xxxs', 'xs'],
    py: 'xxxs',
  },

  xxs: {
    px: ['xxs', 'sm'],
    py: 'xxs',
  },
  xs: {
    px: ['xs', 'md'],
    py: 'xs',
  },
  sm: {
    px: ['sm', 'lg'],
    py: 'sm',
  },
  md: {
    px: ['md', 'xl'],
    py: 'md',
  },
  lg: {
    px: ['md', 'xxl'],
    py: 'lg',
  },
  xl: {
    px: ['md', 'xxxl'],
    py: 'xl',
  },
}

export const buttonSizes = {
  xxxs: {
    px: ['0', '0'],
    py: blockSizes.xxs.py,
  },
  xxs: {
    px: ['xs', 'sm'],
    py: blockSizes.xxs.py,
  },
  xs: {
    px: ['sm', 'md'],
    py: blockSizes.xs.py,
  },
  sm: {
    px: ['md', 'lg'],
    py: blockSizes.sm.py,
  },
  md: {
    px: ['lg', 'xl'],
    py: blockSizes.md.py,
  },
  lg: {
    px: ['xl', 'xxl'],
    py: blockSizes.lg.py,
  },
  xl: {
    px: ['xxl', 'xxxl'],
    py: blockSizes.xl.py,
  },
}

/**
 * A map between known component type names allowing us to provide
 * consistent spacing
 *
 * @see ContentStack for an example usage
 */
export const componentSpacing: Record<
  string,
  Record<string, keyof typeof space> & { default: keyof typeof space }
> = {
  Detail: {
    default: 'lg',
    Headline: 'lg',
  },
  Headline: {
    default: 'xl',
  },
  Heading: {
    default: 'lg',
    Detail: 'xs',
  },
  SubHeading: {
    default: 'lg',
    Body: 'md',
  },
  Body: {
    default: 'lg',
    Body: 'md',
  },
  default: {
    default: 'lg',
  },
}

export const Button = {
  baseStyle: {
    fontFamily: 'mono',
    fontSize: 'sm',
    textTransform: 'uppercase',
    letterSpacing: 'tight',
    borderWidth: '1px',
    _disabled: { cursor: 'default' },
  },
  sizes: buttonSizes,
  variants: {
    primary: {
      ...layerStyles.orange,
      _disabled: {
        ...layerStyles.orangeDisabled,
      },
    },
    secondary: {
      ...layerStyles.softBlack,
      _disabled: {
        ...layerStyles.softBlackDisabled,
      },
    },
    tertiary: {
      ...layerStyles.outlined,
      _disabled: {
        ...layerStyles.outlinedDisabled,
      },
    },
    unstyled: {
      border: 0,
    },
  },
  defaultProps: {
    size: 'md',
    variant: 'primary',
  },
}

export const Checkbox = {
  baseStyle: {
    icon: {
      transitionProperty: 'transform',
      transitionDuration: 'normal',
    },
    control: {
      transitionProperty: 'box-shadow',
      transitionDuration: 'normal',
      border: '1px solid',
      borderRadius: 'sm',
      borderColor: 'softBlack',
      width: 'font.md',
      height: 'font.md',
      color: 'softBlack',
      _hover: {
        bg: 'white',
      },
    },
    label: {
      userSelect: 'none',
      fontSize: 'sm',
      ml: 'sm',
      _disabled: { opacity: 0.4 },
    },
  },
  sizes: {
    md: {
      control: { height: '1rem', width: '1rem' },
    },
  },
  defaultProps: {
    size: 'md',
  },
}

export const Input = {
  baseStyle: {
    field: {
      display: 'block',
      width: '100%',
      fontFamily: 'body',
      fontSize: 'md',
      letterSpacing: 'tight',
      bg: 'transparent',
      _disabled: {
        cursor: 'default',
        color: 'softestBlack',
        borderColor: 'softestBlack',
      },
      _invalid: {
        borderBottomColor: 'failRed',
      },
      _placeholder: {
        color: 'softestBlack',
      },
    },
  },
  sizes: {
    xs: {
      field: {
        py: 'xxxs',
      },
    },
    sm: {
      field: {
        py: 'xxxs',
      },
    },
    md: {
      field: {
        py: 'xxs',
      },
    },
    lg: {
      field: {
        py: 'xs',
      },
    },
    xl: {
      field: {
        py: 'md',
      },
    },
  },
  variants: {
    unstyled: {
      field: {
        px: 0,
      },
    },
    primary: {
      field: {
        borderColor: 'softBlack',
        borderBottom: '1px solid',
        color: 'softBlack',
        px: 0,
        _focus: {
          outline: 'none',
        },
      },
    },
  },
  defaultProps: {
    size: 'md',
    variant: 'primary',
  },
}

// How we have setup Chakra UI really really sucks -
// overengineered mess with 0 value.
// Why even use a component library if you insist on overriding
// every single style.
export const Slider = sliderTheme

export const Text = {
  baseStyle: {
    // text color should be defined in a "layer style"
    // see: https://chakra-ui.com/docs/styled-system/text-and-layer-styles
    color: 'inherit',
  },
  sizes: {
    inherit: {},
    xxs: { fontSize: 'xxs' },
    xs: { fontSize: 'xs' },
    sm: { fontSize: 'sm' },
    md: { fontSize: 'md' },
    lg: { fontSize: 'lg' },
    xl: { fontSize: 'xl' },
    xxl: { fontSize: 'xxl' },
    xxxl: { fontSize: 'xxxl' },
    xxxxl: { fontSize: 'xxxxl' },
  },
  variants: textStyles,
  defaultProps: {
    size: 'inherit',
    variant: 'regular',
  },
}

export const Select = {
  baseStyle: {
    field: {
      display: 'block',
      width: '100%',
      fontFamily: 'body',
      fontSize: 'md',
      letterSpacing: 'tight',
      bg: 'transparent',
      appearance: 'none',
      _disabled: {
        cursor: 'default',
        color: 'softestBlack',
        borderColor: 'softestBlack',
      },
    },
    icon: {
      transform: 'translate(-100%, 0)',
      insetEnd: 2,
    },
  },
  sizes: {
    xs: {
      field: blockSizes.xxxs,
    },
    sm: {
      field: blockSizes.xxxs,
    },
    md: {
      field: blockSizes.xxs,
    },
    lg: {
      field: blockSizes.xs,
    },
    xl: {
      field: blockSizes.md,
    },
  },
  variants: {
    primary: {
      field: {
        borderColor: 'softBlack',
        borderBottom: '1px solid',
        px: 0,
        _focus: {
          borderColor: 'softBlack',
          outline: 'none',
        },
        _invalid: {
          borderColor: 'failRed',
        },
      },
      icon: {
        position: 'absolute',
        transform: 'translateY(-50%)',
        top: '50%',
      },
    },
  },
  defaultProps: {
    size: 'md',
    variant: 'primary',
  },
}

export const Switch = {
  baseStyle: {
    track: {
      bg: 'transparent',
      appearance: 'none',
      borderWidth: '1px',
      borderColor: 'softBlack',
      cursor: 'pointer',
      overflow: 'hidden',
      padding: '1px',
      transition: '0.1s linear background-color',
      _disabled: {
        cursor: 'default',
        bg: 'softestBlack',
        borderColor: 'softestBlack',
      },
      _checked: {
        bg: 'orange',
      },
    },
    thumb: {
      bg: 'softBlack',
      borderRadius: 'full',
      transition: '0.1s linear transform',
    },
  },
  sizes: {
    xs: {
      track: {
        height: 'font.xs',
        width: `calc(${fontSizes.xs} * 2)`,
        borderRadius: fontSizes.xs,
      },
      thumb: {
        height: 'font.xs',
        width: 'font.xs',
        _checked: { transform: `translate(${fontSizes.xs}, 0)` },
      },
    },
    sm: {
      track: {
        height: 'font.sm',
        width: `calc(${fontSizes.sm} * 2)`,
        borderRadius: fontSizes.sm,
      },
      thumb: {
        height: 'font.sm',
        width: 'font.sm',
        _checked: { transform: `translate(${fontSizes.sm}, 0)` },
      },
    },
    md: {
      track: {
        height: 'font.md',
        width: `calc(${fontSizes.md} * 2)`,
        borderRadius: fontSizes.md,
      },
      thumb: {
        height: 'font.md',
        width: 'font.md',
        _checked: { transform: `translate(${fontSizes.md}, 0)` },
      },
    },
    lg: {
      track: {
        height: 'font.lg',
        width: `calc(${fontSizes.lg} * 2)`,
        borderRadius: fontSizes.lg,
      },
      thumb: {
        height: 'font.lg',
        width: 'font.lg',
        _checked: { transform: `translate(${fontSizes.lg}, 0)` },
      },
    },
    xl: {
      track: {
        height: 'font.xl',
        width: `calc(${fontSizes.xl} * 2)`,
        borderRadius: fontSizes.xl,
      },
      thumb: {
        height: 'font.xl',
        width: 'font.xl',
        _checked: { transform: `translate(${fontSizes.xl}, 0)` },
      },
    },
  },
  variants: {
    primary: {},
  },
  defaultProps: {
    size: 'md',
    variant: 'primary',
  },
}

export const Tooltip = {
  baseStyle: {
    fontFamily: 'mono',
    fontSize: 'xs',
    textTransform: 'uppercase',
    letterSpacing: 'tight',
    maxWidth: 'prose',
    _disabled: { cursor: 'default' },
    zIndex: 'modal',
  },
  sizes: blockSizes,
  variants: {
    primary: layerStyles.softBlack,
  },
  defaultProps: {
    size: 'xs',
    variant: 'primary',
  },
}

// To match default height with other input elements
// we can match the padding, font-size and line-height
// and do a height calculation to override chakra UI's default height.
// NB. 1.5 is the inherited line-height in our html
const calcHeight = (
  pY: string,
  fontSize = fontSizes.md,
  lineHeight = htmlBaseLineHeight,
  borderWidth = '1px'
): string =>
  `calc(calc(${fontSize} * ${lineHeight}) + calc(${pY} * 2) + ${borderWidth})`

export const Textarea = {
  baseStyle: {
    verticalAlign: 'top',
    display: 'block',
    width: '100%',
    fontFamily: 'body',
    fontSize: 'md',
    letterSpacing: 'tight',
    bg: 'transparent',
    _disabled: {
      cursor: 'default',
      color: 'softestBlack',
      borderColor: 'softestBlack',
    },
    _invalid: {
      borderBottomColor: 'failRed',
    },
    _placeholder: {
      color: 'softestBlack',
    },
  },
  sizes: {
    xs: {
      py: 'xxxs',
      minHeight: calcHeight(space.xxxs),
      height: calcHeight(space.xxxs),
    },
    sm: {
      py: 'xxxs',
      minHeight: calcHeight(space.xxxs),
      height: calcHeight(space.xxxs),
    },
    md: {
      py: 'xxs',
      minHeight: calcHeight(space.xxs),
      height: calcHeight(space.xxs),
    },
    lg: {
      py: 'xs',
      minHeight: calcHeight(space.xs),
      height: calcHeight(space.xs),
    },
    xl: {
      py: 'md',
      minHeight: calcHeight(space.md),
      height: calcHeight(space.md),
    },
  },
  variants: {
    unstyled: {
      px: 0,
    },
    primary: {
      borderColor: 'softBlack',
      borderBottom: '1px solid',
      color: 'softBlack',
      px: 0,
      _focus: {
        outline: 'none',
      },
    },
    boxed: {
      borderColor: 'gray',
      borderWidth: 1,
      borderStyle: 'solid',
      color: 'softBlack',
      px: 'md',
      _focus: {
        outline: 'none',
      },
    },
  },
  defaultProps: {
    size: 'md',
    variant: 'primary',
  },
}

export const Toast = {
  baseStyle: {
    fontFamily: 'mono',
    fontSize: 'xs',
    textTransform: 'uppercase',
    letterSpacing: 'tight',
    maxWidth: 'prose',
    _disabled: { cursor: 'default' },
  },
  sizes: blockSizes,
  variants: {
    primary: layerStyles.white,
  },
  defaultProps: {
    size: 'xs',
    variant: 'primary',
  },
}

export const Tabs = {
  baseStyle: {
    tab: {
      ...textStyles.cta,
    },
  },
  sizes: {
    md: {
      tab: { px: 'lg', py: 'sm' },
    },
  },
  defaultProps: {
    size: 'md',
  },
}

export const Modal = {
  baseStyle: {
    overlay: {
      bg: 'rgba(0, 0, 0, 0.23)',
      zIndex: 'overlay',
    },
    dialogContainer: {
      display: 'block',
      zIndex: 'modal',
      justifyContent: 'center',
      alignItems: 'center',
      overflow: 'auto',
    },
    dialog: {
      bg: 'white',
      color: 'softBlack',
      my: 'xxxl',
      mx: 'auto',
      zIndex: 'modal',
      maxWidth: 'article',
    },
    header: { p: 'sm' },
    closeButton: {
      position: 'absolute',
      top: '2',
      insetEnd: '3',
    },
    body: { flex: 1, p: 'sm' },
    footer: {
      p: 'sm',
      bg: 'softWhite',
      borderTopWidth: '1px',
      borderColor: 'gray',
    },
  },
  sizes: {
    md: {},
    lg: {
      dialog: {
        maxWidth: '80vw',
      },
    },
  },
  defaultProps: { size: 'md' },
}

export const Table = {
  baseStyle: {
    table: {
      fontVariantNumeric: 'lining-nums tabular-nums',
      borderCollapse: 'collapse',
      width: 'full',
    },
    th: {
      textTransform: 'uppercase',
      letterSpacing: 'wider',
      textAlign: 'start',
      padding: 'sm',
    },
    td: {
      textAlign: 'start',
      padding: 'sm',
    },
    caption: {
      fontFamily: 'heading',
      textAlign: 'center',
      fontWeight: 'medium',
    },
    tbody: {
      tr: {
        '&:nth-of-type(odd)': {
          'th, td': {
            background: 'softestWhite',
          },
        },
      },
    },
  },
  sizes: {},
  defaultProps: {},
}
